import React, { useEffect, useRef, useState } from 'react';

import { ConditionChild, ConditionGroup, PersonalizationFieldGroup } from 'editor/src/store/design/types';
import { groupPersonalizationFields } from 'editor/src/store/design/util/personalizationFieldGroups';
import { useSelector } from 'editor/src/store/hooks';

import styles from './index.module.scss';

interface Props {
  conditionGroup: ConditionGroup;
  elements: ConditionChild[];
  childrenKey: string;
  renderElement: (element: ConditionChild) => React.ReactNode;
}

function FieldGroup({
  group,
  elements,
  conditionGroup,
  renderElement,
}: Omit<Props, 'childrenKey'> & { group: PersonalizationFieldGroup }) {
  const [expanded, setExpanded] = useState(false);
  const selection = useSelector((state) => state.editor.selectedElementUuids.join(','));
  const selectedInGroup = (() => {
    const selected = new Set(selection.split(',').map(Number));
    const visited = new Set<string>();
    function containsSelection(children: ConditionChild[]): boolean {
      return children.some((child) => {
        if (child.type !== 'condition') {
          return selected.has(child.id);
        }
        if (visited.has(child.id)) {
          return false;
        }
        visited.add(child.id);
        const condition = conditionGroup.conditions[child.id];
        return condition
          ? condition.options.some((option) =>
              containsSelection(conditionGroup.children[`${condition.id}-${option.id}`] || []),
            )
          : false;
      });
    }
    return containsSelection(elements) ? selection : '';
  })();
  const previousSelection = useRef(selectedInGroup);
  useEffect(() => {
    if (selectedInGroup && selectedInGroup !== previousSelection.current) {
      setExpanded(true);
    }
    previousSelection.current = selectedInGroup;
  }, [selectedInGroup]);

  return (
    <section className={styles.group} data-personalization-group={group.id}>
      <button type="button" className={styles.heading} aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
        <span>{group.name}</span>
        <span className={styles.count}>{elements.length}</span>
        <span className={styles.chevron} aria-hidden="true" />
      </button>
      <div className={styles.fields} hidden={!expanded}>
        {elements.map((element) => (
          <div key={`${element.type}:${element.id}`} className={styles.field}>
            {renderElement(element)}
          </div>
        ))}
      </div>
    </section>
  );
}

function PersonalizationFieldLayout({ conditionGroup, elements, childrenKey, renderElement }: Props) {
  const sections = groupPersonalizationFields(conditionGroup, elements, childrenKey);
  return (
    <>
      {sections.map((section) =>
        section.group ? (
          <FieldGroup
            key={section.group.id}
            group={section.group}
            elements={section.elements}
            conditionGroup={conditionGroup}
            renderElement={renderElement}
          />
        ) : (
          renderElement(section.element)
        ),
      )}
    </>
  );
}

export default PersonalizationFieldLayout;
